import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUniversityBySlug } from '../data/universityData';
import { CheckCircleIcon, LockIcon, ArrowRightIcon, GraduationCapIcon, BuildingIcon, AwardIcon } from 'lucide-react';
export function BrandedCollegePage() {
  const {
    collegeSlug
  } = useParams<{
    collegeSlug: string;
  }>();
  const navigate = useNavigate();
  const university = collegeSlug ? getUniversityBySlug(collegeSlug) : undefined;
  if (!university) {
    return <div className="min-h-[60vh] flex items-center justify-center bg-gray-50" data-id="element-498">
        <div className="text-center" data-id="element-499">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-500">
            University Not Found
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-501">
            We couldn't find the college you're looking for.
          </p>
          <button onClick={() => navigate('/')} className="bg-[#3b82f6] text-white px-6 py-2.5 rounded-lg font-bold hover:bg-[#2563eb] transition-colors" data-id="element-502">
            Return Home
          </button>
        </div>
      </div>;
  }
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: 60
  }, (_, i) => currentYear - i);
  // Group programs by category
  const programsByCategory = university.programs.reduce((acc, program) => {
    if (!acc[program.category]) {
      acc[program.category] = [];
    }
    acc[program.category].push(program.name);
    return acc;
  }, {} as Record<string, string[]>);
  return <div className="min-h-screen bg-gray-50" data-id="element-503">
      {/* Top Trust Banner */}
      <div className="w-full py-2 px-4 text-center text-white text-sm font-medium flex items-center justify-center gap-2" style={{
      backgroundColor: university.secondaryColor
    }} data-id="element-504">
        <div className="flex gap-0.5" data-id="element-505">
          {[1, 2, 3, 4, 5].map(i => <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 20 20" data-id="element-506">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-507" />
            </svg>)}
        </div>
        Trusted by 70,000+ Future Students
      </div>

      {/* Hero Section */}
      <div className="relative w-full py-16 md:py-24 overflow-hidden" style={{
      backgroundColor: university.primaryColor
    }} data-id="element-508">
        {/* Decorative Background Elements */}
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-black/10 rounded-full blur-3xl pointer-events-none" data-id="element-509"></div>
        <div className="absolute top-20 -right-20 w-80 h-80 bg-white/10 rounded-full blur-2xl pointer-events-none" data-id="element-510"></div>

        <div className="max-w-7xl mx-auto px-4 relative z-10" data-id="element-511">
          {/* Logo Area */}
          <div className="mb-12 md:mb-20 flex items-center gap-3" data-id="element-512">
            <div className="w-10 h-10 bg-white rounded flex items-center justify-center" data-id="element-513">
              <GraduationCapIcon className="w-6 h-6" style={{
              color: university.primaryColor
            }} data-id="element-514" />
            </div>
            <span className="text-2xl font-bold text-white tracking-tight" data-id="element-515">
              {university.logoText}
            </span>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-516">
            {/* Left Column: Content */}
            <div className="text-white" data-id="element-517">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-518">
                {university.name}
              </h1>
              <p className="text-lg md:text-xl text-white/90 leading-relaxed max-w-xl" data-id="element-519">
                {university.mission}
              </p>
            </div>

            {/* Right Column: Lead Form Card */}
            <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 w-full max-w-md mx-auto lg:ml-auto" data-id="element-520">
              {/* Form Header */}
              <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-100" data-id="element-521">
                <div className="flex items-center gap-2" data-id="element-522">
                  <GraduationCapIcon className="w-5 h-5" style={{
                  color: university.primaryColor
                }} data-id="element-523" />
                  <span className="font-bold text-gray-900 text-sm" data-id="element-524">
                    {university.name}
                  </span>
                </div>
                <div className="flex items-center gap-1 bg-green-50 text-green-700 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider border border-green-200" data-id="element-525">
                  <CheckCircleIcon className="w-3 h-3" data-id="element-526" />
                  Verified
                </div>
              </div>

              <p className="text-sm text-gray-600 text-center mb-6" data-id="element-527">
                We'll use your answers to match you with programs tailored to
                your timeline and goals.
              </p>

              {/* Step Indicator */}
              <div className="mb-8" data-id="element-528">
                <div className="text-center text-xs font-bold text-blue-600 mb-2" data-id="element-529">
                  Step 1/3
                </div>
                <div className="flex gap-2" data-id="element-530">
                  <div className="h-1.5 flex-1 bg-blue-600 rounded-full" data-id="element-531"></div>
                  <div className="h-1.5 flex-1 bg-gray-200 rounded-full" data-id="element-532"></div>
                  <div className="h-1.5 flex-1 bg-gray-200 rounded-full" data-id="element-533"></div>
                </div>
              </div>

              {/* Form Fields (Display Only) */}
              <div className="space-y-5" data-id="element-534">
                <div data-id="element-535">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-536">
                    What year did you graduate high school?
                  </label>
                  <select className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none" data-id="element-537">
                    <option data-id="element-538">Please Select</option>
                    {years.map(year => <option key={year} data-id="element-539">{year}</option>)}
                  </select>
                </div>

                <div data-id="element-540">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-541">
                    When would you like to start?
                  </label>
                  <select className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none" data-id="element-542">
                    <option data-id="element-543">Please Select</option>
                    <option data-id="element-544">Immediately</option>
                    <option data-id="element-545">1-3 months</option>
                    <option data-id="element-546">3-6 months</option>
                    <option data-id="element-547">6-12 months</option>
                    <option data-id="element-548">1+ year</option>
                  </select>
                </div>

                <div data-id="element-549">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-550">
                    Which Programs are you interested in?
                  </label>
                  <select className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none" data-id="element-551">
                    <option data-id="element-552">Select a Program</option>
                    {university.programs.map((p, i) => <option key={i} data-id="element-553">{p.name}</option>)}
                  </select>
                </div>

                <div data-id="element-554">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-555">
                    What level of education have you completed?
                  </label>
                  <select className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none" data-id="element-556">
                    <option data-id="element-557">Please Select</option>
                    <option data-id="element-558">Some High School</option>
                    <option data-id="element-559">High School/GED</option>
                    <option data-id="element-560">Some College</option>
                    <option data-id="element-561">Associate's Degree</option>
                    <option data-id="element-562">Bachelor's Degree</option>
                    <option data-id="element-563">Master's Degree</option>
                    <option data-id="element-564">Doctorate</option>
                  </select>
                </div>

                <button onClick={() => navigate('/request-information')} className="w-full py-3.5 text-white font-bold rounded-full text-lg mt-4 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity shadow-md" style={{
                backgroundColor: university.ctaColor
              }} data-id="element-565">
                  Next Step
                  <ArrowRightIcon className="w-5 h-5" data-id="element-566" />
                </button>

                <div className="flex items-center justify-center gap-1.5 mt-4 text-[10px] text-gray-500" data-id="element-567">
                  <LockIcon className="w-3 h-3 text-yellow-500" data-id="element-568" />
                  Your information is secure and only used to match you with
                  relevant programs.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Social Proof Area */}
      <div className="bg-gray-100 py-12 border-b border-gray-200" data-id="element-569">
        <div className="max-w-3xl mx-auto px-4 text-center" data-id="element-570">
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6" data-id="element-571">
            Join over 70,000 people who used Education Directory to get ahead
          </h3>
          <div className="flex justify-center -space-x-4" data-id="element-572">
            {['https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop', 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop', 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop', 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop', 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop'].map((url, i) => <img key={i} src={url} alt="Student" className="w-12 h-12 rounded-full border-2 border-white object-cover" data-id="element-573" />)}
          </div>
        </div>
      </div>

      {/* University Details */}
      <div className="max-w-7xl mx-auto px-4 py-16" data-id="element-574">
        <div className="grid md:grid-cols-3 gap-8 mb-16" data-id="element-575">
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex items-start gap-4" data-id="element-576">
            <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0" data-id="element-577">
              <BuildingIcon className="w-6 h-6 text-blue-600" data-id="element-578" />
            </div>
            <div data-id="element-579">
              <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-580">
                Institution Type
              </h4>
              <p className="text-lg font-semibold text-gray-900" data-id="element-581">
                {university.type}
              </p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex items-start gap-4" data-id="element-582">
            <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0" data-id="element-583">
              <AwardIcon className="w-6 h-6 text-green-600" data-id="element-584" />
            </div>
            <div data-id="element-585">
              <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-586">
                Accreditation
              </h4>
              <p className="text-lg font-semibold text-gray-900" data-id="element-587">
                {university.accreditation}
              </p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex items-start gap-4" data-id="element-588">
            <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0" data-id="element-589">
              <GraduationCapIcon className="w-6 h-6 text-purple-600" data-id="element-590" />
            </div>
            <div data-id="element-591">
              <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-592">
                Founded
              </h4>
              <p className="text-lg font-semibold text-gray-900" data-id="element-593">
                {university.founded}
              </p>
            </div>
          </div>
        </div>

        {/* Programs Grid */}
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-594">
          Available Online Programs
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-595">
          {Object.entries(programsByCategory).map(([category, programs]) => <div key={category} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-596">
              <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-597">
                {category}
              </h3>
              <ul className="space-y-2" data-id="element-598">
                {programs.map((program, idx) => <li key={idx} className="flex items-start gap-2 text-gray-700" data-id="element-599">
                    <CheckCircleIcon className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" data-id="element-600" />
                    <span data-id="element-601">{program}</span>
                  </li>)}
              </ul>
            </div>)}
        </div>
      </div>
    </div>;
}